import React, { useEffect, useRef, useState, useMemo } from "react";
import html2canvas from 'html2canvas';
import TailwindPieChart from '../Tailwind/TailwindPieChart';
import { useChartPalette } from "../Tailwind/chartColors";

export default function PieChartItem({ name, data }) {
    const [clipBoardLoading, setClipBoardLoading] = useState(false);
    const graphRef = useRef(null);
    const palette = useChartPalette();

    useEffect(() => {
        if (clipBoardLoading && graphRef.current) {
            html2canvas(graphRef.current, {
                onclone: (clonedDoc, clonedEl) => {
                    const btn = clonedEl.querySelector('[data-copy-btn]');
                    if (btn) btn.remove();
                    const title = clonedEl.querySelector('h3');
                    if (title) title.style.paddingBottom = '4px';
                },
            }).then(canvas => {
                canvas.toBlob(blob => {
                    const item = new ClipboardItem({ 'image/png': blob });
                    navigator.clipboard.write([item]);
                    setClipBoardLoading(false);
                });
            });
        }
    }, [clipBoardLoading]);

    const handleCopy = () => {
        window.focus();
        setClipBoardLoading(true);
    };

    // Helper to parse value (handles various data structures)
    const parseValue = (value: any): number => {
        // Direct number
        if (typeof value === 'number') return value;

        // String (possibly with %)
        if (typeof value === 'string') {
            const cleaned = value.replace(/%/g, '').trim();
            const parsed = parseFloat(cleaned);
            return isNaN(parsed) ? 0 : parsed;
        }

        // Object with .data property (like other charts in this app)
        if (value && typeof value === 'object') {
            // If it has a data property
            if ('data' in value) {
                const dataValue = value.data;
                // data is an array - sum all values
                if (Array.isArray(dataValue)) {
                    return dataValue.reduce((sum, v) => {
                        const num = typeof v === 'number' ? v : parseFloat(String(v).replace(/%/g, ''));
                        return sum + (isNaN(num) ? 0 : num);
                    }, 0);
                }
                // data is a single value
                return parseValue(dataValue);
            }
            // If it's an array directly
            if (Array.isArray(value)) {
                return value.reduce((sum, v) => {
                    const num = typeof v === 'number' ? v : parseFloat(String(v).replace(/%/g, ''));
                    return sum + (isNaN(num) ? 0 : num);
                }, 0);
            }
        }

        return 0;
    };

    // Transform { name: value } to recharts format [{ name, value, color }]
    // Filter out 0 values as they don't render properly in pie charts
    const chartData = useMemo(() => {
        if (!data || typeof data !== 'object') return [];
        const entries = Object.entries(data);
        return entries
            .map(([entryName, value], index) => ({
                name: entryName,
                value: parseValue(value),
                color: palette[index % palette.length],
            }))
            .filter(item => item.value > 0);
    }, [data, palette]);

    // Check if there's any data to display
    const hasData = chartData.length > 0;

    const chartHeight = useMemo(() => Math.max(300, 300 + (chartData.length - 5) * 20), [chartData.length]);

    return (
        <div className="min-w-0">
            <div ref={graphRef} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm overflow-hidden min-w-0">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-900 truncate">{name}</h3>
                    <button
                        data-copy-btn
                        onClick={handleCopy}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors group relative flex-shrink-0"
                        title="Copy to clipboard"
                    >
                        {!clipBoardLoading ? (
                            <span className="material-symbols-outlined text-[20px] text-slate-500">content_copy</span>
                        ) : (
                            <div className="w-5 h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                        )}
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Copy to clipboard
                        </span>
                    </button>
                </div>
                <div className="min-w-0 w-full">
                    {hasData ? (
                        <TailwindPieChart
                            data={chartData}
                            height={chartHeight}
                            innerRadius={50}
                            showLabels={true}
                            showLegend={true}
                            showTooltip={true}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-slate-500">
                            <div className="text-center">
                                <span className="material-symbols-outlined text-[48px] text-slate-300 mb-2 block">
                                    pie_chart
                                </span>
                                <p>No data to display</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
